import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import { CaseFormSectionContainerComponent } from 'app/shared/components/forms/case-form-section-container/case-form-section-container.component';
import { HelpPageService } from '../help-page.service';
import { BlobStorageService } from 'app/shared/services/blob-storage.service';
import { ToastrService } from 'ngx-toastr';
import { DOCUMENT_LIBRARY_TABLE_CONFIG, DOCUMENT_LIBRARY_TABLE_STATE } from '../help-document-library/help-document-library-table.config';
import { DataTableActionEnum, DataTableColumnFiltersApi, DataTableColumnModel, DataTableConfigSettings, DataTableStateModel } from 'app/shared/components/dynamic-table/dynamic-table.model';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { HelperService } from 'app/shared/services/helper.service';
import { finalize, take } from 'rxjs';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { ListResponse } from 'app/shared/models/list-response.model';
import { DocumentLibraryEntry } from '../help-page.model';
import { BackNavigationComponent } from "../../shared/components/back-navigation/back-navigation.component";

@Component({
  selector: 'app-help-document-view-all',
  standalone: true,
  imports: [CommonModule, CaseFormSectionContainerComponent, DynamicTableComponent, TranslateModule, BackNavigationComponent],
  templateUrl: './help-document-view-all.component.html',
  styleUrl: './help-document-view-all.component.scss',
  providers: [DatePipe],
})
export class HelpDocumentViewAllComponent implements OnInit {
  columns = this.helperService.deepCopy(DOCUMENT_LIBRARY_TABLE_CONFIG);
  filters: DataTableColumnFiltersApi[] = [];
  cases: any = [];
  totalCount: number = 0;
  datatableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(DOCUMENT_LIBRARY_TABLE_STATE.gridName);
  dataTableState: DataTableStateModel = this.datatableStateFromStorage ? this.datatableStateFromStorage : { ...DOCUMENT_LIBRARY_TABLE_STATE };
  isDataTableReady: boolean = false;
  defaultColumns = this.helperService.deepCopy(DOCUMENT_LIBRARY_TABLE_CONFIG);
  selectedFilterName = '';

  constructor(
    private router: Router,
    private helpPageService: HelpPageService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private translate: TranslateService,
    private blobStorageService: BlobStorageService,
    private configurationService: ConfigurationService,
    private helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.getDataTableConfiguration();
  }

  fetchDocuments(): void {
    this.helpPageService.getDocuments(this.dataTableState).subscribe((res: ListResponse<DocumentLibraryEntry[]>) => {
      this.isDataTableReady = true;
      this.cases = res.items;
      this.totalCount = res.totalCount;
    });
  }

  actionButton(data: any): void {
    if (data.action === DataTableActionEnum.DELETE) {
      const modalRef = this.modalService.open(ConfirmationDialogComponent, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'confirmation-modal',
      });

      const titleText = data.row.name ? data.row.name : 'label.delete_document';
      modalRef.componentInstance.title = `${titleText}`;
      modalRef.componentInstance.message = 'messages.are_you_sure_you_want_to_delete_this_document';
      modalRef.componentInstance.notConfirmActionLabel = 'label.cancel';
      modalRef.componentInstance.confirmActionLabel = 'label.confirm';

      modalRef.result.then((result) => {
        if (result) {
          if (data.row.id) {
            this.deleteDocumentAndFile(data.row.id, data.row.storageFileId);
          } else {
            this.cases.splice(data.rowIndex, 1);
            this.cases = [...this.cases];
          }
        }
      });
    }
  }

  onLinkedValueClick(row: any): void {
    const file: DocumentLibraryEntry = row.row;
    if (!file?.storageFileId) {
      this.toastr.show(this.translate.instant('messages.document_download_error'), ToastTypesEnum.ERROR);
      return;
    }

    this.blobStorageService.downloadFile(file.storageFileId).subscribe((blob) => {
      this.helperService.saveFile(file, blob);
    }, () => {
      this.toastr.show(this.translate.instant('messages.document_download_error'), ToastTypesEnum.ERROR);
    });
  }


  deleteDocumentAndFile(documentId: number, storageFileId: string): void {
    this.helpPageService.deleteDocument(documentId).subscribe(() => {
      this.blobStorageService.deleteFile(storageFileId).subscribe(() => {
        const successMessage = this.translate.instant('messages.document_deleted_success');
        this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);
        this.fetchDocuments();
      });
    });
  }

  getDataTableState(event: any): void {
    this.dataTableState = event;
    this.fetchDocuments();
  }

  goBack(): void {
    this.router.navigate(['/help-page']);
  }

  onSaveDatatableSettings(data: DataTableStateModel) {
    const dataForSave = this.helperService.deepCopy(data);

    if (dataForSave && dataForSave.datatableColumns) {
      dataForSave.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dataTableState.gridName,
      name: dataForSave.datatableFilterName || '',
      isGlobal: dataForSave.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForSave.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForSave.datatableColumns) || ''
    };


    this.configurationService.setDataTableConfiguration(params)
      .pipe(take(1))
      .subscribe();
  }

  getDataTableConfiguration() {
    this.configurationService.getInitDataTableConfiguration(this.dataTableState.gridName)
      .pipe(
        take(1),
        finalize(() => {
          this.fetchDocuments();
        })
      )
      .subscribe((res: DataTableConfigSettings) => {
        if (this.datatableStateFromStorage) {
          this.columns = this.dataTableState.datatableColumns;
          this.filters = this.dataTableState.datatableFilters ?? [];
        } else {
          if (res && res.gridColumnSettings && !!JSON.parse(res.gridColumnSettings).length) {
            this.columns = JSON.parse(res.gridColumnSettings);
            this.dataTableState.datatableColumns = JSON.parse(res.gridColumnSettings);
          }

          if (res && res.gridFilterSettings && !!JSON.parse(res.gridFilterSettings).length) {
            this.filters = JSON.parse(res.gridFilterSettings);
            this.dataTableState.datatableFilters = JSON.parse(res.gridFilterSettings);
          }
        }

        if (res && res.name) {
          this.selectedFilterName = res.name;
        }

      });
  }

  onUpdateDatatableSettings(data: DataTableStateModel) {
    const dataForUpdate = this.helperService.deepCopy(data);

    if (dataForUpdate && dataForUpdate.datatableColumns) {
      dataForUpdate.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dataTableState.gridName,
      name: dataForUpdate.datatableFilterName || '',
      isGlobal: dataForUpdate.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForUpdate.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForUpdate.datatableColumns) || ''
    };

    const filterId = dataForUpdate.datatableFilterId;

    this.configurationService.updateSavedFilter(filterId, params)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.show(this.translate.instant('messages.filter_successfully_updated'), ToastTypesEnum.SUCCESS);
        },
        error: (err) => {
          this.toastr.show(this.translate.instant('messages.failed_to_update_filter'), ToastTypesEnum.ERROR);
          console.error(err);
        }
      });
  }
}
